import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import parse from "html-react-parser";
import UnstyledImage from "./image";
import { Link } from "gatsby";
import { faArrowRightLong } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const Wrapper = styled.div.attrs(
  ({ $letterSpacing, $rowSpacing, $linkColor }) => ({
    $letterSpacing: $letterSpacing || "",
    $rowSpacing: $rowSpacing || "",
    $linkColor: $linkColor || "",
  })
)`
  display: flex;
  flex-direction: column;
  margin: 0.6rem 0;
  flex: 1;
  width: auto;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    width: 50%;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    width: ${({ $twoCol }) => ($twoCol ? "50%" : "100%")};
    margin: ${({ $twoCol }) => ($twoCol ? "0" : "1rem 0")};
    padding: 5px;
  }
  letter-spacing: ${({ $letterSpacing }) => $letterSpacing + "px"};
  line-height: ${({ $rowSpacing }) => $rowSpacing};
  a {
    color: ${({ $linkColor }) => $linkColor};
  }
`;

const ImageDiv = styled(Link)`
  height: 400px;
  overflow: hidden;
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    height: 350px;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    height: ${({ $twoCol }) => ($twoCol ? "178px" : "315px")};
  }
`;
const Image = styled(UnstyledImage)`
  max-width: 100%;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease-in-out !important;

  &:hover {
    transform: scale(1.04);
    cursor: pointer;
  }
`;
const Title = styled(Link)`
  padding: 0;
  font-family: ${({ theme }) => theme.fontHeadings.fontFamily};
  font-weight: 300;
  font-size: 1.3rem;
  text-decoration: none;
  color: ${({ $textColor }) => $textColor};
  margin: 0.8rem 0 0 0;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    padding: 1rem 0 0 0;
    font-size: ${({ $twoCol }) => ($twoCol ? "0.9rem" : "1.1rem")};
    font-weight: ${({ $twoCol }) => ($twoCol ? "600" : "300")};
  }
`;
const Description = styled.div`
  padding: 0.5rem 0;
  font-family: ${({ theme }) => theme.fontParagraphs.fontFamily};
  font-size: 0.9rem;
  line-height: 1.4;
  font-weight: 400;
  margin-top: 0;
  color: ${({ $textColor }) => $textColor};
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    padding: 0.5rem 0;
    margin: 0;
    font-size: ${({ $twoCol }) => ($twoCol ? "0.8rem" : "0.9rem")};
  }
  & p {
    margin: 0;
    font-size: 0.9rem;
  }
`;
const CardLink = styled(Link)`
  padding: 0;
  text-decoration: none;
  color: ${({ theme, $buttonTextColor }) =>
    $buttonTextColor || theme.colors.bodyLink};
  font-family: ${({ theme }) => theme.fontLinks.fontFamily};
  font-size: 15px;
  width: fit-content;
  display: flex;
  gap: 0.3rem;
  &:hover {
    text-decoration: underline;
    span {
      padding-left: 15px;
    }
  }
  span {
    margin-top: 5px;
    transition: all 0.3s ease;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    margin: 15px 0;
    padding: 0;
    width: 100%;
  }
`;
const Arrow = styled(FontAwesomeIcon)`
  color: ${({ theme, $buttonTextColor }) =>
    $buttonTextColor || theme.colors.bodyLink};
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    padding-left: 0.5rem;
  }
`;

export default function Card({
  title,
  description,
  imgSrc,
  image,
  imgAlt,
  label,
  linkUrl,
  twoCol,
  backgroundColor,
  textColor,
  buttonTextColor,
  letterSpacing,
  rowSpacing,
  linkColor,
}) {
  return (
    <Wrapper
      $twoCol={twoCol}
      $backgroundColor={backgroundColor}
      $letterSpacing={letterSpacing}
      $rowSpacing={rowSpacing}
      $linkColor={linkColor}
    >
      <ImageDiv to={linkUrl} $twoCol={twoCol}>
        <Image src={imgSrc} alt={imgAlt} image={image} />
      </ImageDiv>
      <Title
        $twoCol={twoCol}
        to={linkUrl}
        dangerouslySetInnerHTML={{ __html: title }}
        $textColor={textColor}
      />
      <Description $twoCol={twoCol} $textColor={textColor}>
        {parse(description)}
      </Description>
      {label && (
        <CardLink to={linkUrl} $buttonTextColor={buttonTextColor}>
          <span dangerouslySetInnerHTML={{ __html: label }} />
          <span>
            <Arrow
              $buttonTextColor={buttonTextColor}
              icon={faArrowRightLong}
              size="1x"
            />
          </span>
        </CardLink>
      )}
    </Wrapper>
  );
}

Card.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  imgSrc: PropTypes.object,
  image: PropTypes.object,
  imgAlt: PropTypes.string,
  label: PropTypes.string,
  linkUrl: PropTypes.string.isRequired,
  twoCol: PropTypes.bool,
  backgroundColor: PropTypes.string,
  textColor: PropTypes.string,
  buttonTextColor: PropTypes.string,
  letterSpacing: PropTypes.string,
  rowSpacing: PropTypes.string,
  linkColor: PropTypes.string,
};
Card.defaultProps = {
  twoCol: false,
  imgSrc: undefined,
  image: undefined,
  imgAlt: undefined,
  label: undefined,
};
